import {
  Box,
  FormControl,
  FormHelperText,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import NumberFormat from "react-number-format";

type Props = {
  prevValue: {
    calcType: string;
    value: number;
  };
  label: string;
  type: string;
  calculusOptions: keyValueObj[];
  helperText?: string;
  disabled: boolean;
  disableValue?: boolean;
  disableCurrency?: boolean;
  foreignDecimalScale?: number;
  updateCalcValues(type: string, value: number, calcType: string): void;
};
type keyValueObj = {
  value: string;
  label: string;
};

const ChangelingMonetaryInput: React.FC<Props> = ({
  prevValue,
  label,
  type,
  calculusOptions,
  updateCalcValues,
  helperText,
  disabled,
  disableValue = false,
  disableCurrency = false,
  foreignDecimalScale = 4,
}: Props) => {
  const [value, setValue] = useState(0);
  const [calcType, setCalcType] = useState(type === "ISS" ? "PERCENTAGE" : "");
  const isEditingRef = useRef(false);

  const sxOptions = {
    boxShadow: "none",
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderStyle: "none solid none none",
      borderColor: "#777",
      borderWidth: "1px",
      borderRadius: 0,
    },
    ".MuiOutlinedInput-notchedOutline": {
      borderStyle: "none solid none none",
    },
    width: "100%",
  };
  const renderMenuOptions = (typeOptions: keyValueObj[]) => {
    return typeOptions.map((option) => (
      <MenuItem value={option.value}>{option.label}</MenuItem>
    ));
  };
  const handleChange = async (v: any) => {
    if (calcType === "BRL") {
      setValue(1);
      return;
    }
    if (v.floatValue != null) {
      setValue(parseFloat(v.floatValue));
    } else {
      setValue(0);
    }
  };
  const handleBlur = () => {
    if (calcType === "" && value === 0) {
      isEditingRef.current = false;
      return;
    }
    if (calcType === "BRL") {
      updateCalcValues(type, 1, calcType);
    } else {
      updateCalcValues(type, value, calcType);
    }
    // Clear after parent commits so a same-tick prop sync can't wipe the typed value.
    queueMicrotask(() => {
      isEditingRef.current = false;
    });
  };
  const syncStateFromProps = (nextValue: {
    calcType: string;
    value: number;
  }) => {
    if (nextValue.calcType) {
      setCalcType(nextValue.calcType);
      if (nextValue.calcType === "BRL") {
        setValue(1);
      } else if (nextValue.value) {
        setValue(nextValue.value);
      } else {
        setValue(0);
      }
    } else if (nextValue.value) {
      setValue(nextValue.value);
    } else {
      setValue(0);
    }
  };

  // Depend on primitives — callers pass a fresh prevValue object every render.
  useEffect(() => {
    if (isEditingRef.current) {
      return;
    }

    syncStateFromProps(prevValue);
  }, [prevValue.value, prevValue.calcType]);

  // 1. Crie uma referência para o elemento input
  const inputRef = useRef<any>(null);

  // Função para lidar com o foco e selecionar o texto
  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    isEditingRef.current = true;
    e.target.select();
  };

  // Função para lidar com o clique e selecionar o texto
  const handleInputClick = (e: React.MouseEvent<HTMLInputElement>) => {
    const target = e.target as HTMLInputElement;
    if (target && typeof target.select === "function") {
      setTimeout(() => {
        target.select();
      }, 0);
    }
  };

  // Função para lidar com o mouseDown e selecionar o texto
  const handleMouseDown = (e: React.MouseEvent<HTMLInputElement>) => {
    const target = e.target as HTMLInputElement;
    if (target && typeof target.select === "function") {
      e.preventDefault();
      target.focus();
      setTimeout(() => {
        target.select();
      }, 0);
    }
  };
  return (
    <Box
      display={"flex"}
      flexDirection={"column"}
      alignItems={"flex-start"}
      width={"100%"}
      my={1}
    >
      <InputLabel sx={{ fontSize: 13 }}>{label}</InputLabel>
      {type !== "NFMM" ? (
        <NumberFormat
          sx={{
            background: disabled ? "#eee" : "#FFF",
            "& .MuiInputBase-input": {
              border: 0,
              marginLeft: "-15px",
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <FormControl
                  sx={{
                    m: 1,
                    minWidth: 70,
                    padding: 0,
                    border: 0,
                  }}
                >
                  <Select
                    SelectDisplayProps={{ tabIndex: -1 }}
                    disableUnderline={true}
                    sx={sxOptions}
                    size="small"
                    id="simple-select"
                    placeholder="Selecione"
                    disabled={disabled || disableCurrency}
                    value={calcType}
                    onChange={(e) => {
                      const newCalcType = e.target.value;
                      setCalcType(newCalcType);
                      if (newCalcType === "BRL") {
                        setValue(1);
                        updateCalcValues(type, 1, newCalcType);
                      } else if (newCalcType) {
                        updateCalcValues(type, value, newCalcType);
                      }
                    }}
                    onBlur={handleBlur}
                    MenuProps={{
                      style: { maxHeight: "450px" },
                      anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "left",
                      },
                      transformOrigin: {
                        vertical: "top",
                        horizontal: "left",
                      },
                    }}
                  >
                    <MenuItem value="">
                      <em>Não selecionado</em>
                    </MenuItem>
                    {renderMenuOptions(calculusOptions)}
                  </Select>
                </FormControl>
              </InputAdornment>
            ),
          }}
          value={value}
          onValueChange={handleChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          onMouseDown={handleMouseDown}
          onClick={handleInputClick}
          prefix={
            calcType &&
            !["PERCENTUAL", "PERCENTAGE", "PERCENTAGE_RJ"].includes(calcType)
              ? "R$ "
              : undefined
          }
          suffix={undefined}
          allowEmptyFormatting
          decimalSeparator=","
          thousandSeparator="."
          decimalScale={
            calcType !== "BRL" && calcType !== "" ? foreignDecimalScale : 2
          }
          placeholder="Digite um valor"
          customInput={TextField}
          inputRef={inputRef}
          color={"info"}
          size="small"
          style={{
            border: 0,
          }}
          disabled={
            disabled || disableValue || calcType === "" || calcType === "BRL"
          }
          helperText={helperText}
        />
      ) : (
        <FormControl fullWidth>
          <Select
            fullWidth
            disableUnderline={true}
            size="small"
            id="simple-select"
            placeholder="Selecione"
            onBlur={handleBlur}
            value={calcType}
            disabled={disabled}
            onChange={(e) => setCalcType(e.target.value)}
          >
            <MenuItem value="">
              <em>Não selecionado</em>
            </MenuItem>
            {renderMenuOptions(calculusOptions)}
          </Select>
          {helperText && (
            <FormHelperText>
              A - Cobrar automaticamente apenas FEE
            </FormHelperText>
          )}
        </FormControl>
      )}
    </Box>
  );
};

export default ChangelingMonetaryInput;
